//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  DISTRIBUTION_ENDPOINTS,
  IContentLibraryItem,
} from '@apis/content-library/contentLibraryApiModel';
import { Caption } from '@components/atoms/caption/Caption';
import Media from '@components/atoms/media/Media';
import { getContentLibraryItemMediaType } from '@components/atoms/media/getMediaType';
import {
  MetricList,
  RateMetric,
  ValueMetric,
} from '@components/atoms/metric-list/MetricList';
import Source from '@components/atoms/source/Source';
import { RelativeTimestamp } from '@components/atoms/timestamp/Timestamp';
import DetailsSheet from '@components/molecules/details-sheet/DetailsSheet';
import MediaCard from '@components/molecules/media-card/MediaCard';
import { useCaptionStatus } from '@hooks/useCaptionStatus';
import { ProcessingVideo } from '@legacy-components/common/processing-video/ProcessingVideo';
import {
  GRID_CONTENT_CARD_HEIGHT,
  GRID_CONTENT_CARD_HEIGHT_NO_METADATA,
  GRID_CONTENT_CARD_WIDTH,
} from '@legacy-components/content/library-grid-content/constants';
import Cuid from '@models/Cuid';
import PartialCuid from '@models/PartialCuid';
import { SocialNetwork } from '@models/SocialNetwork';
import { UniversalImageActionsView } from '@models/UniversalImageActionsView';
import { UniversalItemType } from '@models/UniversalItemType';
import { getContentItemDetailsService } from '@react-bridge/wrappers/ng-service-provider';
import { getClientId } from '@redux/selectors/getClientId';
import { Mixins } from '@theme/mixins';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import { isDefined } from '@utils/filterPredicates';
import { getEngagementFieldNames } from '@utils/getEngagementFieldNames';
import { toGrammaticallyCorrectList } from '@utils/stringUtils';
import { openUia } from '@utils/uia';

import { RIGHTS_STATUS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';
import { CAPTIONS_STATUS_VALUE } from 'app/constants/mute-tooltip-content';

interface Props {
  item: IContentLibraryItem;
  disableMetadata: boolean;
}

export const LibraryGridCard = ({ disableMetadata, item }: Props) => {
  const [productTagsCount, setProductTagsCount] = useState(0);
  const [distributionEndpoints, setDistributionEndpoints] = useState<
    DISTRIBUTION_ENDPOINTS[]
  >([]);
  const [rightsStatus, setRightsStatus] = useState<undefined | RIGHTS_STATUS>();
  const [muteStatus, setMuteStatus] = useState<boolean | undefined>();
  const [captionStatus, setCaptionStatus] = useState<boolean | undefined>();

  const captionSelectedItems = useSelector(
    (state) => state.bulkActions.selectedItemsForCaptions,
  );
  const captionTurnOnOffState = useSelector(
    (state) => state.bulkActions.captionTurnOnOffState,
  );
  const clientId = useSelector(getClientId);
  const cuid = Cuid.build({ itemType: item.itemType, id: item.id }, clientId);

  const captionStatusValue: string | undefined = captionSelectedItems[cuid];

  const isCaptionInProgress =
    captionTurnOnOffState === 'on' &&
    captionStatusValue === CAPTIONS_STATUS_VALUE.IN_PROGRESS;

  useCaptionStatus(captionTurnOnOffState, captionStatusValue, setCaptionStatus);
  useEffect(() => {
    if (item) {
      const contentLibraryItemDetailsService = getContentItemDetailsService();
      const partialCuid = PartialCuid.build(item);
      const itemDetailsStream =
        contentLibraryItemDetailsService.requestItemDetails(partialCuid, false);

      itemDetailsStream.subscribe((itemDetailsMap) => {
        const itemDetails = itemDetailsMap?.[partialCuid];

        if (itemDetails) {
          setProductTagsCount(itemDetails.productTagsCount);
          setDistributionEndpoints(itemDetails.distributionEndpoints);
          setRightsStatus(itemDetails.rightsStatus);
          setMuteStatus(itemDetails.muteStatus);
          setCaptionStatus(itemDetails.captionStatus);
        }
      });
    }
  }, [item]);

  const image =
    item.videoStatus === 'Processing' ? (
      <StyledProcessingVideo />
    ) : (
      <StyledMedia
        id={item.id}
        imageUrl={item.imageUrl}
        itemType={item.itemType}
        metadataLayer={{
          enabled: true,
          isCaptionInProgress: isCaptionInProgress,
          mediaType: getContentLibraryItemMediaType(item),
          itemDetails: {
            productTagsCount,
            distributionEndpoints,
            rightsStatus,
            muteStatus,
            captionStatus,
          },
        }}
        onItemClick={(e) => {
          openUia(
            { itemId: item.id, itemType: item.itemType },
            UniversalImageActionsView.Details.name,
            () => null,
            e,
          );
        }}
      />
    );

  return (
    <StyledMediaCard
      bottom={!disableMetadata ? <CardBottom item={item} /> : undefined}
      disableMetadata={disableMetadata}
      id={item.id}
      image={image}
      itemType={item.itemType}
      top={!disableMetadata ? CardTop(item) : undefined}
    />
  );
};

const getItemSocialNetwork = (item: IContentLibraryItem) => {
  // IG story posts are currently mapped as Facebook content in the backend, which requires additional logic to properly identify them as Instagram content
  // TODO: PD-298332 - Remove this workaround after IG story is properly mapped as Instagram content in the backend
  if (
    item.socialDetails?.socialNetwork === SocialNetwork.Facebook &&
    (item.socialDetails.isInstagramStory ||
      isDefined(item.socialDetails.instagramPostType))
  ) {
    return SocialNetwork.Instagram;
  }

  return item.socialDetails?.socialNetwork;
};

const CardTop = (item: IContentLibraryItem) => {
  const clientId = useSelector(getClientId);
  const cuid = Cuid.build({ itemType: item.itemType, id: item.id }, clientId);

  const { data: metadata, isError, isLoading } = useMediaMetadataQuery(cuid);

  const metadataUser = {
    fullName: metadata?.socialDetails.networkUserDisplayName,
    id: metadata?.socialDetails.networkUserId,
    profilePicture: metadata?.socialDetails.networkUserImageUrl,
    username: metadata?.networkUsername,
  };

  const [networkUsername, networkUserDisplayName] =
    item.itemType === UniversalItemType.MediaImport
      ? [undefined, item.misDetails?.user.fullName]
      : [
          item.socialDetails?.user?.username ?? metadataUser.username,
          item.socialDetails?.user?.fullName ?? metadataUser.fullName,
        ];

  // If this item isn't a MIS item, then we're probably missing username information due to graph api limitations
  const missingUsernameHelpText = (() => {
    switch (item.itemType) {
      case UniversalItemType.Facebook:
      case UniversalItemType.Instagram:
        return 'Due to Instagram API limitations, we are unable to display information for this user.';
      default:
        return undefined;
    }
  })();

  return (
    <StyledHeaderWrapper>
      <Source
        ingestionSource={item.ingestionSource}
        itemInfo={{
          originalUrl: item.socialDetails?.link,
          socialNetwork: getItemSocialNetwork(item),
        }}
        missingUsernameHelpText={missingUsernameHelpText}
        universalItemType={item.itemType}
        userInfo={{
          networkUserImageUrl:
            item.socialDetails?.user?.profilePicture ??
            metadataUser.profilePicture,
          networkUsername,
          networkUserDisplayName,
        }}
      />
    </StyledHeaderWrapper>
  );
};

const CardBottom = ({ item }: { item: IContentLibraryItem }) => {
  const socialDetailsSheetSlot =
    item.description || item.socialDetails?.stats ? (
      <DetailsSheetContainer>{SocialDetailsSheet(item)}</DetailsSheetContainer>
    ) : (
      <DetailsPlaceholder />
    );

  return (
    <StyledMetadataWrapper>
      {socialDetailsSheetSlot}
      <VerticalSpacer heightPx={16} />
      {item.createdTime && (
        <RelativeTimestamp ellipsize={1} timestamp={item.createdTime} />
      )}
    </StyledMetadataWrapper>
  );
};

const SocialDetailsSheet = (item: IContentLibraryItem) => {
  const isBVReview = item.itemType === UniversalItemType.Bazaarvoice;

  const engagementFieldsText = toGrammaticallyCorrectList(
    getEngagementFieldNames(
      item.socialDetails?.socialNetwork ?? SocialNetwork.Unknown,
    ),
  );

  return (
    <DetailsSheetContainer>
      <DetailsSheet
        sheetContent={
          <>
            {item.socialDetails?.stats && (
              <StyledMetricList>
                {!isBVReview && (
                  <RateMetric
                    label='Engagement Rate'
                    tooltip={`Follower Engagement Rate: Sum of ${engagementFieldsText} divided by the number of current followers.`}
                    value={
                      item.socialDetails.stats.followerEngagementRate &&
                      item.socialDetails.stats.followerEngagementRate * 100
                    }
                  />
                )}
                {!isBVReview && (
                  <RateMetric
                    label='Reach Engagement Rate'
                    tooltip={`Engagement Rate: Sum of ${engagementFieldsText} divided by the number of unique users who have seen this media.`}
                    value={
                      item.socialDetails.stats.reachEngagementRate &&
                      item.socialDetails.stats.reachEngagementRate * 100
                    }
                  />
                )}
                {!isBVReview && (
                  <ValueMetric
                    label='Comments'
                    tooltip='Comments: Number of comments this post received.'
                    value={item.socialDetails.stats.comments}
                  />
                )}
                <ValueMetric
                  label='Likes'
                  tooltip='Likes: Number of times this post was liked.'
                  value={item.socialDetails.stats.likes}
                />
              </StyledMetricList>
            )}
            <StyledCaption
              caption={item.description ?? ''}
              shouldDecorateLinks={true}
              socialNetwork={item.socialDetails?.socialNetwork}
            />
          </>
        }
      >
        <DetailsText>Details</DetailsText>
      </DetailsSheet>
    </DetailsSheetContainer>
  );
};

const StyledMediaCard = styled(MediaCard)<{ disableMetadata: boolean }>(
  ({ disableMetadata }) => `
  height: ${
    disableMetadata
      ? GRID_CONTENT_CARD_HEIGHT_NO_METADATA
      : GRID_CONTENT_CARD_HEIGHT
  }px;
  width: ${GRID_CONTENT_CARD_WIDTH}px;
`,
);

const StyledMedia = styled(Media)`
  height: ${GRID_CONTENT_CARD_HEIGHT_NO_METADATA}px;
  width: ${GRID_CONTENT_CARD_WIDTH}px;
`;

const StyledMetadataWrapper = styled.div(
  ({ theme }) => `
  padding: ${theme.spacings[3]} ${theme.spacings[2]};
  display: flex;
  flex-direction: column;
`,
);

const StyledHeaderWrapper = styled.div(
  ({ theme }) => `
  padding: ${theme.spacings[2]} ${theme.spacings[3]} ${theme.spacings[2]} ${theme.spacings[2]};
  overflow: hidden;
`,
);

const StyledMetricList = styled(MetricList)(
  ({ theme }) => `
  margin-bottom: calc(${theme.spacings[1]} * 3);
  width: 100%;
`,
);

const StyledCaption = styled(Caption)(
  ({ theme }) => `
  /* The caption should always be 4 lines of text tall */
  margin-bottom: calc(${theme.spacings[1]} * 3);
`,
);

const DetailsText = styled.a`
  ${Mixins.Text.LinkPrimary}
  cursor: pointer;
`;

const DetailsPlaceholder = styled.div`
  ${Mixins.Text.LinkPrimary}
  height: 1.5em;
`;

const VerticalSpacer = styled.div<{ heightPx: number }>`
  flex-shrink: 0;
  height: ${({ heightPx }) => heightPx}px;
`;

const DetailsSheetContainer = styled.div`
  display: inline-block;
`;

const StyledProcessingVideo = styled(ProcessingVideo)`
  .processing-video .video-processing-text {
    bottom: inherit;
  }

  .processing-video {
    width: inherit;
    border-left: none;
    border-right: none;
  }

  height: ${GRID_CONTENT_CARD_HEIGHT_NO_METADATA}px;
  width: ${GRID_CONTENT_CARD_WIDTH}px;
  display: inline-block;
`;
